import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const RelatedServices = () => {
  const [searchParams] = useSearchParams();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const currentArea = searchParams.get('area') || 'corporate-law';

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const allPracticeAreas = {
    'corporate-law': {
      en: {
        title: 'Corporate Law',
        description: 'Comprehensive business legal solutions for companies of all sizes.',
        icon: 'Building2'
      },
      sw: {
        title: 'Sheria ya Makampuni',
        description: 'Suluhisho kamili za kisheria za biashara kwa makampuni ya ukubwa wote.',
        icon: 'Building2'
      }
    },
    'family-law': {
      en: {
        title: 'Family Law',
        description: 'Compassionate legal representation for family matters and disputes.',
        icon: 'Heart'
      },
      sw: {
        title: 'Sheria ya Familia',
        description: 'Uwakilishi wa kisheria wa huruma kwa mambo ya familia na migogoro.',
        icon: 'Heart'
      }
    },
    'property-law': {
      en: {
        title: 'Property Law',
        description: 'Expert guidance on real estate transactions and property disputes.',
        icon: 'Home'
      },
      sw: {
        title: 'Sheria ya Mali',
        description: 'Uongozi wa kitaalamu katika miamala ya mali na migogoro ya mali.',
        icon: 'Home'
      }
    },
    'criminal-defense': {
      en: {
        title: 'Criminal Defense',
        description: 'Aggressive defense representation for criminal charges.',
        icon: 'Shield'
      },
      sw: {
        title: 'Utetezi wa Jinai',
        description: 'Uwakilishi wa utetezi mkali kwa mashtaka ya jinai.',
        icon: 'Shield'
      }
    }
  };

  // Filter out current practice area
  const relatedAreas = Object.entries(allPracticeAreas)
    .filter(([key]) => key !== currentArea)
    .slice(0, 3);

  const handleWhatsAppClick = (areaTitle) => {
    const message = encodeURIComponent(currentLanguage === 'en' 
      ? `Hello, I would like to inquire about your ${areaTitle} services.`
      : `Hujambo, ningependa kuuliza kuhusu huduma zenu za ${areaTitle}.`);
    window.open(`https://wa.me/254721320787?text=${message}`, '_blank');
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
            {currentLanguage === 'en' ? 'Related Legal Services' : 'Huduma Zinazohusiana za Kisheria'}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentLanguage === 'en' ?'Explore our other practice areas and discover how we can assist with your various legal needs.' :'Chunguza maeneo yetu mengine ya utendaji na ugundua jinsi tunavyoweza kusaidia na mahitaji yako mbalimbali ya kisheria.'
            }
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {relatedAreas.map(([areaKey, areaData]) => {
            const content = areaData[currentLanguage];
            return (
              <div
                key={areaKey}
                className="group bg-gradient-to-br from-surface/5 to-primary/5 rounded-2xl p-8 border border-border-light hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="text-center space-y-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl group-hover:scale-110 transition-transform duration-300">
                    <Icon name={content.icon} size={28} color="white" strokeWidth={2} />
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-text-primary mb-3">
                      {content.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed">
                      {content.description}
                    </p>
                  </div>

                  <div className="flex flex-col space-y-3">
                    <Link
                      to={`/individual-practice-area-detail?area=${areaKey}`}
                      className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-accent transition-colors duration-200"
                    >
                      <Icon name="ArrowRight" size={20} className="mr-2" />
                      {currentLanguage === 'en' ? 'Learn More' : 'Jifunze Zaidi'}
                    </Link>
                    
                    <button
                      onClick={() => handleWhatsAppClick(content.title)}
                      className="inline-flex items-center justify-center px-6 py-3 border border-primary text-primary rounded-xl font-medium hover:bg-primary hover:text-white transition-all duration-200"
                    >
                      <Icon name="MessageCircle" size={20} className="mr-2" />
                      {currentLanguage === 'en' ? 'Inquire Now' : 'Uliza Sasa'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Practice Areas */}
        <div className="mt-12 text-center">
          <Link
            to="/practice-areas-overview"
            className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-medium hover:shadow-lg transition-all duration-200"
          >
            <Icon name="Grid3x3" size={20} className="mr-2" />
            {currentLanguage === 'en' ? 'View All Practice Areas' : 'Ona Maeneo Yote ya Utendaji'}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RelatedServices;